import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import { displayName } from '../data/mockClients'
import type { ClientProfile, ClientStatus } from '../data/types'
import { ApiError } from '../api/auth'
import { listClients } from '../api/clients'
import { getHashQuery, navigate } from '../router/hash'
import { AppShell } from '../layout/AppShell'
import { Avatar } from '../components/Avatar'
import { Icon, type IconName } from '../components/Icon'
import { FacesheetPrintModal } from '../components/FacesheetPrintModal'
import { FacesheetSettingsModal } from '../components/FacesheetSettingsModal'
import { ColumnsSettingsModal } from '../components/ColumnsSettingsModal'
import { PrintSettingsModal } from '../components/PrintSettingsModal'
import { ExportSettingsModal } from '../components/ExportSettingsModal'
import { MissingProfileDataModal } from '../components/MissingProfileDataModal'
import { statusBadgeClass } from '../data/nav'
import { LIST_COLUMNS_SETTINGS } from '../data/listColumnsSettings'
import { getPreference } from '../api/preferences'

type Filter = 'All' | ClientStatus

type ColumnsPreference = {
  checked: Record<string, boolean>
  priority: Record<string, string>
  statusFilter: string
}

const DEFAULT_COLUMNS: ColumnsPreference = {
  checked: Object.fromEntries(LIST_COLUMNS_SETTINGS.rows.map((row) => [row.value, row.checked])),
  priority: Object.fromEntries(LIST_COLUMNS_SETTINGS.rows.map((row) => [row.value, row.priority])),
  statusFilter: LIST_COLUMNS_SETTINGS.statusFilter,
}

const FILTERS: Filter[] = ['All', 'Approved', 'Visitor', 'Hold', 'Discharged', 'Potential']

type SettingsKey =
  | 'facesheetPrint'
  | 'facesheetSettings'
  | 'columns'
  | 'print'
  | 'export'
  | 'missing'

const SETTINGS_ITEMS: { key: SettingsKey; label: string; icon: IconName }[] = [
  { key: 'facesheetPrint', label: 'Facesheet Print', icon: 'audit' },
  { key: 'facesheetSettings', label: 'Facesheet Settings', icon: 'admin' },
  { key: 'columns', label: 'Columns Settings', icon: 'grid' },
  { key: 'print', label: 'Print Settings', icon: 'audit' },
  { key: 'export', label: 'Export Settings', icon: 'reports' },
  { key: 'missing', label: 'Missing Profile Data', icon: 'shield' },
]

export function ClientsListPage({ currentPath }: { currentPath: string }) {
  const [query, setQuery] = useState(() => getHashQuery('q'))
  const [filter, setFilter] = useState<Filter>('All')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const settingsRef = useRef<HTMLDivElement>(null)
  const [facesheetPrintOpen, setFacesheetPrintOpen] = useState(false)
  const [facesheetSettingsOpen, setFacesheetSettingsOpen] = useState(false)
  const [columnsSettingsOpen, setColumnsSettingsOpen] = useState(false)
  const [printSettingsOpen, setPrintSettingsOpen] = useState(false)
  const [exportSettingsOpen, setExportSettingsOpen] = useState(false)
  const [missingProfileOpen, setMissingProfileOpen] = useState(false)
  const [clients, setClients] = useState<ClientProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [columnsPreference, setColumnsPreference] = useState<ColumnsPreference>(DEFAULT_COLUMNS)

  const loadClients = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      setClients(await listClients())
    } catch (error) {
      setLoadError(error instanceof ApiError ? error.message : 'Unable to load clients from the API.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadClients()
  }, [loadClients])

  const loadColumnsPreference = useCallback(async () => {
    try {
      const saved = await getPreference<ColumnsPreference>('clients.columns')
      if (saved) setColumnsPreference(saved)
    } catch {
      // Keep the captured defaults when preferences are unavailable.
    }
  }, [])

  useEffect(() => {
    void loadColumnsPreference()
  }, [loadColumnsPreference])

  const statusScopedClients = useMemo(() => {
    const status = columnsPreference.statusFilter
    if (status === 'Alls' || !status) return clients
    if (status === 'AV') return clients.filter((client) => client.status === 'Approved' || client.status === 'Visitor')
    return clients.filter((client) => client.status === status)
  }, [clients, columnsPreference.statusFilter])

  const counts = useMemo(() => {
    const count = (status: ClientStatus) => statusScopedClients.filter((client) => client.status === status).length
    return {
      all: statusScopedClients.length,
      approved: count('Approved'),
      visitors: count('Visitor'),
      hold: count('Hold'),
      discharged: count('Discharged'),
      potential: count('Potential'),
    }
  }, [statusScopedClients])

  useEffect(() => {
    if (!settingsOpen) return
    const onDoc = (e: MouseEvent) => {
      const el = settingsRef.current
      if (el && !el.contains(e.target as Node)) setSettingsOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSettingsOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [settingsOpen])

  const openSetting = (key: SettingsKey) => {
    setSettingsOpen(false)
    if (key === 'facesheetPrint') setFacesheetPrintOpen(true)
    else if (key === 'facesheetSettings') setFacesheetSettingsOpen(true)
    else if (key === 'columns') setColumnsSettingsOpen(true)
    else if (key === 'print') setPrintSettingsOpen(true)
    else if (key === 'export') setExportSettingsOpen(true)
    else setMissingProfileOpen(true)
  }

  const countFor = (f: Filter): number => {
    if (f === 'All') return counts.all
    if (f === 'Approved') return counts.approved
    if (f === 'Visitor') return counts.visitors
    if (f === 'Hold') return counts.hold
    if (f === 'Discharged') return counts.discharged
    return counts.potential
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return statusScopedClients.filter((c) => {
      if (filter !== 'All' && c.status !== filter) return false
      if (!q) return true
      const hay = `${c.firstName} ${c.lastName} ${c.id} ${c.phoneCell || ''} ${c.phoneHome || ''}`.toLowerCase()
      return hay.includes(q)
    })
  }, [statusScopedClients, query, filter])

  const visibleColumns = useMemo(() => LIST_COLUMNS_SETTINGS.rows
    .filter((row) => columnsPreference.checked[row.value])
    .sort((a, b) => Number(columnsPreference.priority[a.value] || 999) - Number(columnsPreference.priority[b.value] || 999)),
  [columnsPreference])

  const profileText = (client: ClientProfile, ...keys: string[]) => {
    for (const key of keys) {
      const value = client.profile?.[key]
      if (typeof value === 'string' && value.trim()) return value
      if (typeof value === 'number') return String(value)
    }
    return '—'
  }

  const renderColumn = (column: string, client: ClientProfile): ReactNode => {
    if (column === 'PHOTO') return <Avatar client={client} />
    if (column === 'CLIENT NAME') return <div><b>{displayName(client)}</b><small className="client-id-line">ID {client.id}</small></div>
    if (column === 'PAYER NAME') return client.payerName || profileText(client, 'cli_payer_name')
    if (column === 'ADMISSION DATE') return client.admissionDate || profileText(client, 'cli_adm_date')
    if (column === 'PHONE NO') return client.phoneCell || client.phoneHome || '—'
    if (column === 'EMAIL ID') return client.email || profileText(client, 'cli_email')
    if (column === 'DCC STATUS') return <span className={statusBadgeClass(client.status)}>{client.status}</span>
    if (column === 'CARE MANAGER') return client.careManager || profileText(client, 'cli_mltss_manager_name')
    if (column === 'CLIENT ID') return client.id
    if (column === 'DATE OF BIRTH') return client.dob || '—'
    if (column === 'MEDICAID ID') return client.medicaidId || profileText(client, 'cli_medicaid')
    if (column === 'ADDRESS') return client.address || profileText(client, 'cli_address')
    if (column === 'TRANSPORTATION BROKER') return profileText(client, 'cli_third_party_transportation')
    if (column === 'MOBILITY ID #') return profileText(client, 'cli_mobility_id')
    if (column === 'HMO ID') return profileText(client, 'cli_hmo_id')
    if (column === 'LOC') return profileText(client, 'cli_loc')
    return '—'
  }

  const openClient = (id: string) => navigate(`/clients/${id}/overview`)

  const rowAction = (e: ReactMouseEvent, id: string, tab: string) => {
    e.stopPropagation()
    navigate(`/clients/${id}/${tab}`)
  }

  return (
    <AppShell
      currentPath={currentPath}
      title="Clients"
      subtitle={`${counts.all} people in your roster`}
      search={{
        value: query,
        onInput: setQuery,
        placeholder: 'Search by name, ID or phone…',
      }}
      actions={
        <>
          <div className={`popover-wrap${settingsOpen ? ' is-open' : ''}`} ref={settingsRef}>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              aria-haspopup="menu"
              aria-expanded={settingsOpen}
              onClick={() => setSettingsOpen((v) => !v)}
            >
              <Icon name="admin" /> List settings
            </button>
            {settingsOpen ? (
              <div className="popover-menu" role="menu">
                {SETTINGS_ITEMS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className="popover-menu__item"
                    role="menuitem"
                    onClick={() => openSetting(item.key)}
                  >
                    <Icon name={item.icon} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => navigate('/clients/new')}
          >
            <Icon name="plus" /> New client
          </button>
        </>
      }
    >
      <div className="container">
        <div className="toolbar">
          <div className="chips">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                className={`chip${filter === f ? ' is-active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f}
                <span className="chip__count">{countFor(f)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="table-wrap">
          {loadError ? (
            <div className="empty">
              <p>{loadError}</p>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => void loadClients()}>
                Try again
              </button>
            </div>
          ) : null}
          <table className="data-table">
            <thead>
              <tr>
                {visibleColumns.map((column) => <th key={column.value}>{column.label}</th>)}
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && !loadError ? filtered.map((c) => (
                <tr key={c.id} onClick={() => openClient(c.id)}>
                  {visibleColumns.map((column) => (
                    <td key={column.value} className={column.value === 'DCC STATUS' || column.value === 'PHOTO' ? undefined : 'muted'}>
                      {renderColumn(column.value, c)}
                    </td>
                  ))}
                  <td>
                    <div className="cell-actions">
                      <button
                        type="button"
                        className="act-btn"
                        title="Open profile"
                        onClick={(e) => rowAction(e, c.id, 'overview')}
                      >
                        <Icon name="eye" />
                      </button>
                      <button
                        type="button"
                        className="act-btn"
                        title="Dashboard"
                        onClick={(e) => rowAction(e, c.id, 'dashboard')}
                      >
                        <Icon name="chart" />
                      </button>
                      <button
                        type="button"
                        className="act-btn"
                        title="Audit report"
                        onClick={(e) => rowAction(e, c.id, 'audit')}
                      >
                        <Icon name="audit" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : null}
            </tbody>
          </table>

          {loading ? (
            <div className="empty"><p>Loading clients…</p></div>
          ) : loadError ? null : filtered.length === 0 ? (
            <div className="empty">
              <div>
                <Icon name="search" />
              </div>
              <p>No clients match “{query}”.</p>
            </div>
          ) : (
            <div className="list-foot">
              <span>
                Showing {filtered.length} of {counts.all} clients
              </span>
            </div>
          )}
        </div>
      </div>

      <FacesheetPrintModal
        open={facesheetPrintOpen}
        onClose={() => setFacesheetPrintOpen(false)}
      />
      <FacesheetSettingsModal
        open={facesheetSettingsOpen}
        onClose={() => setFacesheetSettingsOpen(false)}
      />
      <ColumnsSettingsModal
        open={columnsSettingsOpen}
        onClose={() => setColumnsSettingsOpen(false)}
        onSaved={() => void loadColumnsPreference()}
      />
      <PrintSettingsModal
        open={printSettingsOpen}
        onClose={() => setPrintSettingsOpen(false)}
      />
      <ExportSettingsModal
        open={exportSettingsOpen}
        onClose={() => setExportSettingsOpen(false)}
      />
      <MissingProfileDataModal
        open={missingProfileOpen}
        onClose={() => setMissingProfileOpen(false)}
      />
    </AppShell>
  )
}
